import React, { useEffect, useState } from 'react';
import { Header, Table, Message, Button, Toast, Modal, Icon, Label } from 'react-frontier';
import { Expense, Department } from '../SiteClasses';
import { addCommas, getDepartmentName } from '../SiteUtil';
import { Dropdown } from 'semantic-ui-react';
import classNames from 'classnames';
import API from '../API';

interface ModalProps{
	open: boolean,
	onClose?: ()=>void,
	onComplete: (approvedIds: number[])=>void,
}

var ApproveExpensesModal = (props: ModalProps)=>{
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [selectedExpenses, setSelectedExpenses] = useState<number[]>([]);
	var [loading, setLoading] = useState<boolean>(false);
	var [pendingExpenses, setPendingExpenses] = useState<Expense[]>(null);
	var [departmentFilters, setDepartmentFilters] = useState<Department[]>([]);

	useEffect(()=>{
		if(!props.open) return;
		get();
	}, [props.open]);

	useEffect(()=>{
		if(!props.open || pendingExpenses === null) return;
		get();
	}, [departmentFilters]);

	var get = ()=>{
		setPendingExpenses(null);
		API.getExpenses(100, 0, true, departmentFilters).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setPendingExpenses(res.data);

			if(res.data && res.data.length === 1){
				setSelectedExpenses([res.data[0].expense_id]);
			}
		}).catch(err=>{
			setModalPrompts(['Hubo un error cargando los gastos pendientes. (AEM-1)']);
			setPendingExpenses([]);
		});
	};

	var approve = ()=>{
		if(selectedExpenses.length === 0 || !pendingExpenses || pendingExpenses.length === 0) return setModalPrompts(['No hay gastos seleccionados para aprobar.']);
		
		setLoading(true);
		API.approveExpense(selectedExpenses).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Gastos aprobados!');

			props.onClose && props.onClose();
			props.onComplete(selectedExpenses);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al aprobar los gastos. (AEM-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	return <div>
		<Modal open={props.open} size={pendingExpenses?.length > 1 ? 'large' : 'tiny'} onClose={props.onClose}>
			<Modal.Header>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					Aprobar gastos
					<Dropdown
						text='Filtrar'
						icon='filter'
						labeled
						button
						style={{ color: 'black', fontSize: 14 }}
						className='icon tiny'
					>
						<Dropdown.Menu style={{ right: 0, left: 'auto' }}>
							<Dropdown.Item icon='close' text='Limpiar filtros' onClick={() => setDepartmentFilters([])} />
							<Dropdown.Header content='Departamentos' />
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.OPERACIONES) 
										? prev.filter(d => d !== Department.OPERACIONES)
										: [...prev, Department.OPERACIONES]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.OPERACIONES),
									'square outline': !departmentFilters.includes(Department.OPERACIONES),
								})}></i>
								Operaciones
							</Dropdown.Item>
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.COMERCIAL) 
										? prev.filter(d => d !== Department.COMERCIAL)
										: [...prev, Department.COMERCIAL]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.COMERCIAL),
									'square outline': !departmentFilters.includes(Department.COMERCIAL),
								})}></i>
								Comercial
							</Dropdown.Item>
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.DESARROLLO) 
										? prev.filter(d => d !== Department.DESARROLLO)
										: [...prev, Department.DESARROLLO]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.DESARROLLO),
									'square outline': !departmentFilters.includes(Department.DESARROLLO),
								})}></i>
								Desarrollo
							</Dropdown.Item>
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.RECURSOS_HUMANOS) 
										? prev.filter(d => d !== Department.RECURSOS_HUMANOS)
										: [...prev, Department.RECURSOS_HUMANOS]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.RECURSOS_HUMANOS),
									'square outline': !departmentFilters.includes(Department.RECURSOS_HUMANOS),
								})}></i>
								Recursos Humanos
							</Dropdown.Item>
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.MARKETING) 
										? prev.filter(d => d !== Department.MARKETING)
										: [...prev, Department.MARKETING]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.MARKETING),
									'square outline': !departmentFilters.includes(Department.MARKETING),
								})}></i>
								Marketing
							</Dropdown.Item>
							<Dropdown.Item onClick={() => {
								setDepartmentFilters(prev => 
									prev.includes(Department.CONTABILIDAD) 
										? prev.filter(d => d !== Department.CONTABILIDAD)
										: [...prev, Department.CONTABILIDAD]
								);
							}}>
								<i className={classNames('icon', {
									'check square': departmentFilters.includes(Department.CONTABILIDAD),
									'square outline': !departmentFilters.includes(Department.CONTABILIDAD),
								})}></i>
								Contabilidad
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</Modal.Header>
			{pendingExpenses === null ? (
				<Modal.Content>
					<Header loading text='Cargando gastos pendientes...' />
				</Modal.Content>
			) : pendingExpenses.length === 0 ? (
				<Modal.Content>
					<Header text='Sin gastos pendientes' subtext='No hay gastos pendientes en la selección.' />
					<Message type='warning' centered>
						<div style={{ fontSize: 14 }}>
							Todos los gastos seleccionados ya están aprobados o eliminados.
						</div>
					</Message>
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
			) : pendingExpenses.length === 1 ? (
				<Modal.Content>
					<Header text='¿Aprobar gasto?' subtext={'Marcará el gasto como aprobado'} />
					<Table
						details
						title='Información'
						data={[
							['ID', pendingExpenses[0].expense_id],
							['Título', pendingExpenses[0].expense_title],
							['Evento', pendingExpenses[0].event_name || <Icon name='minus' />],
							['Ciudad', pendingExpenses[0].city || <Icon name='minus' />],
							['Creador', pendingExpenses[0].user_creator_name || <Icon name='minus' />],
							['Tipo', pendingExpenses[0].type_name],
							['Departamento', pendingExpenses[0].department_name || <Icon name='minus' />],
							['Monto', '$' + addCommas(pendingExpenses[0].amount)]
						]}
					/>
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
			) : (
				<Modal.Content style={{ maxHeight: 700, overflowY: 'auto', overflowX: 'hidden' }}>
					<div style={{ textAlign: 'center' }}>
						<Button text='Seleccionar todos' color='black' onClick={() => {
							setSelectedExpenses(selectedExpenses.length === pendingExpenses.length ? [] : pendingExpenses.map(e => e.expense_id));
						}}/>
					</div>
					<Table
						selectable
						collapsingIndexes={[0, 1, 3, 4]}
						fitted
						style={{ marginTop: 15 }}
						centeredIndexes={[3]}
						onClick={(v: number[]) => {
							var arr = [...selectedExpenses];
							var ix = arr.indexOf(v[1]);
							if(ix === -1) arr.push(v[1]);
							else arr.splice(ix, 1);
							setSelectedExpenses(arr);
						}}
						headers={[
							<div style={{ textAlign: 'center' }}>
								<i className="checkmark icon" style={{ marginRight: 0 }}></i>
							</div>,
							'ID',
							'TÍTULO',
							'Evento',
							'Ciudad',
							'Creador',
							'DEPARTAMENTO',
							'MONTO'
						]}
						data={pendingExpenses.map(e=>{
							var selected = selectedExpenses.indexOf(e.expense_id) !== -1;
							return[
								<i className={classNames('large icon circle', {
									check: selected,
									outline: !selected
								})}></i>,
								e.expense_id,
								e.expense_title,
								e.event_name ? e.event_name : <Icon name='minus' />,
								e.city || <Icon name='minus' />,
								e.user_creator_name ? e.user_creator_name : <Icon name='minus' />,
								e.department_name ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(e.department_id)}</Label> : <Icon name='minus' />,
								'$' + addCommas(e.amount)
							];
						})}
					/>
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={props.onClose} />
				<Button 
					text='Aprobar' 
					color='black' 
					onClick={approve} 
					loading={loading}
				/>
			</Modal.Actions>
		</Modal>
	</div>;
}

export default ApproveExpensesModal;
